import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import generateId from 'src/lib/generateId';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import Input from 'src/presentation/components/forms/Input';
import MultiselectChips from 'src/presentation/components/forms/MultiselectChips';
import Textarea from 'src/presentation/components/forms/Textarea';
import agentProfilesResource from 'src/actions/agent/agentProfilesResource';
import teamsResource from 'src/actions/teams/teamsResource';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import { useResourceValues } from 'src/presentation/hooks/useResource';

const defaultItem = {
  agentsList: [],
  description: '',
  memberCount: 0,
  name: '',
};

const useOnSave = setComplete => {
  const dispatch = useDispatch();
  return ({ add, isNew, item, resetErrors, update, validate, workingStateRef }) => {
    dispatch(resetErrors());
    const currentWorkingState = workingStateRef.current;
    const workingStateToUpdate = {
      ...omit(currentWorkingState, ['agentsList', 'memberCount']),
      id: isNew ? generateId() : currentWorkingState.id,
    };

    if (!dispatch(validate(workingStateToUpdate))) {
      return;
    }
    setComplete(true);

    isNew ? dispatch(add({}, workingStateToUpdate)) : dispatch(update({ id: item.id }, workingStateToUpdate));
  };
};

const normalizeAgentList = ({ id: value, name, email }) => {
  const config: { label: string; value: string; description?: string } = {
    label: name || email,
    value,
  };
  if (name) {
    config.description = email;
  }
  return config;
};

const TeamEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { values: agentsList } = useResourceValues(agentProfilesResource);
  const { persistingState } = useResourceValues(teamsResource);
  const [isConfirmCancelModalVisible, setConfirmCancelModalVisible] = useState(false);
  const { resetErrors } = teamsResource.actions;
  const { add, update, validate } = teamsResource.effects;

  const handleStay = useCallback(() => {
    setConfirmCancelModalVisible(false);
  }, [setConfirmCancelModalVisible]);

  const [isComplete, setComplete] = useState(false);

  const handleOnSave = useOnSave(setComplete);

  const formContext = useForm({
    add,
    update,
    validate,
    errors,
    item,
    root,
    onSave: handleOnSave,
    resetErrors,
    defaultItem,
  });

  const { isNew, onClose, onSave, workingState } = formContext;
  const isModified = useMemo(() => !isEqual(item || defaultItem, workingState), [item, workingState]);
  const confirmCancel = useCallback(() => {
    isModified ? setConfirmCancelModalVisible(true) : onClose();
  }, [isModified, onClose, setConfirmCancelModalVisible]);

  const dataAidPrefix = 'admin-teams-editor';
  const header = isNew ? 'Create Team' : `Edit ${item.name}`;
  const isUpdating = persistingState === 'persisting';
  const options = agentsList?.map(normalizeAgentList) || [];
  const submitTitle = isUpdating ? 'Saving' : 'Save';
  return (
    <>
      <Form context={formContext}>
        <H1>{header}</H1>
        <Body data-aid={dataAidPrefix}>
          <Section>
            <Row>
              <Input
                data-aid={`${dataAidPrefix}-name-input`}
                attr="name"
                autoFocus
                label="Name"
                placeholder="Enter a descriptive name"
              />
            </Row>
            <Row>
              <Textarea
                data-aid={`${dataAidPrefix}-purpose-input`}
                attr="description"
                label="Purpose"
                placeholder="Describe what this team does"
              />
            </Row>
            <Row>
              <MultiselectChips attr="agentIds" data-aid="members" label="Members" options={options} />
            </Row>
          </Section>
        </Body>

        <Footer>
          <Button data-aid="editor-cancel" type={Button.Types.TEXT} onClick={confirmCancel}>
            Cancel
          </Button>
          <Button data-aid="editor-save" onClick={onSave} isLoading={isUpdating}>
            {submitTitle}
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Team"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
};

export default TeamEditor;
